import DOMPurify from 'dompurify';
import { cn } from '../../../lib/utils';

// Mirrors the BE's own allowlist (inbound.HTMLPolicy, richtext.Sanitize) — client-side sanitizing
// is defense in depth, not the only layer: the BE already sanitizes both an
// inbound creator reply and an outbound composer's rich text before storage.
const RICH_BODY_ALLOWED_TAGS = [
  'p', 'br', 'div', 'span', 'b', 'strong', 'i', 'em', 'u', 's', 'ul', 'ol', 'li',
  'blockquote', 'pre', 'code', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'table', 'thead', 'tbody', 'tr', 'td', 'th', 'hr', 'a',
];
// target/rel are set on links by the hook below, never taken from the mail.
const RICH_BODY_ALLOWED_ATTR = ['href', 'class'];
const SAFE_HREF = /^(?:https?:|mailto:)/i;
const QUOTE_CLASS = 'gmail_quote';
const QUOTED_SELECTOR = `blockquote, div.${QUOTE_CLASS}`;

// A private instance, so these hooks never touch another DOMPurify caller in the app.
const purifier = DOMPurify(window);
purifier.addHook('afterSanitizeAttributes', (node) => {
  if (node.hasAttribute('class')) {
    // Mail can carry any class name, and the app's own utility classes would apply to it.
    if (node.classList.contains(QUOTE_CLASS)) node.setAttribute('class', QUOTE_CLASS);
    else node.removeAttribute('class');
  }
  if (node.tagName !== 'A') return;
  // A sender's own target/rel never survive: every link opens in a new tab with no opener (reverse tabnabbing).
  node.setAttribute('target', '_blank');
  node.setAttribute('rel', 'noopener noreferrer');
});

export function sanitizeRichBody(html: string): string {
  return purifier.sanitize(html, {
    ALLOWED_TAGS: RICH_BODY_ALLOWED_TAGS,
    ALLOWED_ATTR: RICH_BODY_ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: SAFE_HREF,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });
}

/** The reply without its quoted history (Gmail's quote block, any blockquote), the same cut the BE's visible text makes. */
export function splitQuotedHistory(html: string): { visible: string; hasQuoted: boolean } {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const quoted = doc.body.querySelectorAll(QUOTED_SELECTOR);
  // A quote block with no words in it hides nothing, so there is nothing to offer back.
  if (!Array.from(quoted).some((el) => (el.textContent ?? '').trim() !== '')) return { visible: html, hasQuoted: false };
  quoted.forEach((el) => el.remove());
  // A mail that is all quote keeps it all, rather than showing an empty bubble.
  if ((doc.body.textContent ?? '').trim() === '') return { visible: html, hasQuoted: false };
  return { visible: doc.body.innerHTML, hasQuoted: true };
}

/** Mail formatting rendered for reading: sanitised, links and lists styled. */
export function RichBody({ html, className }: { html: string; className?: string }) {
  return (
    <div
      className={cn(
        'wrap-break-word [&_a]:text-(--brand-500) [&_a]:underline [&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground [&_ol]:list-decimal [&_ol]:pl-5 [&_table]:block [&_table]:max-w-full [&_table]:overflow-x-auto [&_ul]:list-disc [&_ul]:pl-5',
        className,
      )}
      dangerouslySetInnerHTML={{ __html: sanitizeRichBody(html) }}
    />
  );
}
